<template>
  <div>
    <template v-if="minimize">
      <pf-mini-tag :style="miniStyles" :id="`mini_${id}`" :content="content"></pf-mini-tag>
    </template>
    <div v-else :class="['pf-annotation-bubble__container', classObj]" :style="containerStyles">
      <div class="pf-annotation-bubble-text" ref="bubbleText">
        <div
          class="pf-annotation--resizer"
          v-if="!dragging || !configuration"
          @mousedown="configuration ? repositioning($event, false) : null"
        >
          <div
            class="pf-annotation--resizer_handle left x"
            ref="leftHandle"
            @mousedown="resizing($event, 'leftHandle')"
          >
            <div class="pf-annotation--resizer_inner"></div>
          </div>
          <div
            class="pf-annotation--resizer_handle right x"
            ref="rightHandle"
            @mousedown="resizing($event, 'rightHandle')"
          >
            <div class="pf-annotation--resizer_inner"></div>
          </div>
        </div>
        <div
          class="pf-annotation-bubble-text__header-container"
          @click="configuration ? clicked($event) : null"
        >
          <span
            :class="['pf-annotation-bubble-text__header', spanClass]"
            :contenteditable="editing"
            @input="editText"
            @change="handler"
          >
            <component :is="headingType">{{ content }}</component>
          </span>
        </div>
      </div>
      <div
        v-if="withTail"
        class="pf-annotation-bubble-tail"
        :style="tailTranslate + tailStyle"
        @mousedown="configuration ? repositioning($event, true) : null"
      >
        <svg class="pf-annotation-bubble-tail__svg" width="1" height="1">
          <polyline class="pf-annotation-bubble-tail__line" :points="tailPoints" />
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
  import { scaleLinear } from 'd3-scale';
  import PfMiniTag from './MiniTag';

  export default {
    name: 'PfCalloutBubble',
    components: {
      PfMiniTag,
    },
    props: {
      id: {
        type: String,
      },
      alignment: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'center', 'right'].indexOf(value) !== -1;
        },
      },
      bold: {
        type: Boolean,
        default: false,
      },
      domainX: {
        type: Array,
        required: true,
      },
      domainY: {
        type: Array,
        required: true,
      },
      headingLevel: {
        type: Number,
        default: 6,
        validator(value) {
          return value >= 1 && value <= 6;
        },
      },
      configuration: {
        type: Boolean,
      },
      content: {
        type: String,
        default: '[Click to edit]',
      },
      height: {
        type: Number,
        default: 0,
      },
      editable: {
        type: Boolean,
        default: false,
      },
      width: {
        type: Number,
        default: 60,
      },
      withTail: {
        type: Boolean,
        default: true,
      },
      x: {
        type: Number,
        required: true,
      },
      y: {
        type: Number,
        required: true,
      },
      z: {
        type: Number,
        default: 2,
      },
      degrees: {
        type: Number,
      },
      direction: {
        type: String,
        default: 'left',
        validator(value) {
          return ['left', 'right'].indexOf(value) !== -1;
        },
      },
      minimize: {
        type: Boolean,
        default: false,
      },
      rangeX: {
        type: Array,
        required: true,
      },
      rangeY: {
        type: Array,
        required: true,
      },
      selected: {
        type: Boolean,
        default: false,
      },
      tail: {
        type: Object,
      },
      linearX: {
        type: Number,
      },
      linearY: {
        type: Number,
      },
    },
    computed: {
      classObj() {
        const obj = {};
        obj['pf-annotation-bubble--config'] = this.configuration;
        obj['pf-annotation-bubble__right'] =
          this.tailPoints === this.topRight || this.tailPoints === this.rightBottom;
        obj['pf-annotation-bubble__bottom'] = this.tailPoints === this.leftBottom;
        obj['pf-annotation-bubble__top'] = this.tailPoints === this.bottomRight;
        obj['pf-annotation-bubble__left'] =
          this.tailPoints === this.topLeft || this.tailPoints === this.leftTop;
        obj['pf-annotation-bubble__mid'] =
          !this.withTail ||
          this.tailPoints === this.topMid ||
          this.tailPoints === this.bottomMid ||
          this.tailPoints === this.leftMid ||
          this.tailPoints === this.rightMid;
        obj['pf-annotation--selected'] = this.selected && this.configuration;

        return obj;
      },
      compHeight() {
        return Number.isNaN(this.confHeight) || this.confHeight == null
          ? this.height
          : this.confHeight;
      },
      compWidth() {
        return Number.isNaN(this.confWidth) || this.confWidth == null ? this.width : this.confWidth;
      },
      containerHeight() {
        return this.withTail ? this.compHeight + this.tailHeight : this.compHeight;
      },
      containerStyles() {
        const resX = this.xScale(this.scaleX);
        const resY = this.yScale(this.scaleY);
        return this.scaleX && this.scaleY
          ? `top: ${resY}px; left: ${resX}px; z-index: ${this.z}; width:  ${
              this.compWidth + this.tailWidth
            }px; height: ${this.containerHeight}px;`
          : null;
      },
      scaleX() {
        return this.dragging || !this.linearX ? this.xScale.invert(this.left) : this.linearX;
      },
      scaleY() {
        return this.dragging || !this.linearY ? this.yScale.invert(this.top) : this.linearY;
      },
      spanClass() {
        const obj = {};

        obj['pf-annotation-bubble-text__header-left'] = this.alignment === 'left';
        obj['pf-annotation-bubble-text__header-center'] = this.alignment === 'center';
        obj['pf-annotation-bubble-text__header-right'] = this.alignment === 'right';
        obj['pf-annotation-bubble-text__header-bold'] = this.bold;

        return obj;
      },
      headingType() {
        return `h${this.headingLevel}`;
      },
      miniStyles() {
        const resX = this.xScale(this.scaleX);
        const resY = this.yScale(this.scaleY);
        return `top: ${resY + this.tailHeight - 10}px; left: ${resX + this.tailWidth}px; z-index: ${
          this.z
        };`;
      },
      textHeight() {
        return this.$refs && this.$refs.bubbleText
          ? this.$refs.bubbleText.getBoundingClientRect().height
          : 0;
      },
      tailStyle() {
        return `width:  ${this.tailWidth}px; height: ${this.tailHeight}px;`;
      },
      tailTranslate() {
        return `transform: translate(0px, ${this.compHeight}px);`;
      },
      topRight() {
        return `0 -1, ${this.tailOrigin} ${this.tailHeight}, ${this.tailWidth} -1`;
      },
      topLeft() {
        return `${this.compWidth} -1, 
          ${this.tailOrigin}  ${this.tailHeight}, ${this.compWidth + this.tailWidth} -1`;
      },
      topMid() {
        const midTail = this.tailWidth * 0.5;
        return `${(this.compWidth - midTail) / 2} -1,
          ${this.tailOrigin} ${this.tailHeight},  ${
          (this.compWidth - midTail) / 2 + this.tailWidth
        } -1`;
      },
      bottomLeft() {
        return `${this.compWidth} ${-this.confHeight + 1}, 
        ${this.tailOrigin} ${this.tailHeight}, ${this.compWidth + this.tailWidth} ${
          -this.compHeight + 1
        }`;
      },
      bottomRight() {
        return `0 ${-this.compHeight + 1}, ${this.tailOrigin}  ${this.tailHeight}, ${
          this.tailWidth
        } ${-this.compHeight + 1}`;
      },
      bottomMid() {
        const midTail = this.tailWidth * 0.5;
        return `${(this.compWidth - midTail) / 2} ${-this.compHeight + 1}, 
          ${this.tailOrigin} ${this.tailHeight},${
          (this.compWidth - midTail) / 2 + this.tailWidth
        } ${-this.compHeight + 1}`;
      },
      leftBottom() {
        return `${this.compWidth + this.tailWidth - 1} ${-this.compHeight}, 
          ${this.tailOrigin}  ${this.tailHeight}, ${this.compWidth + this.tailWidth - 1} ${
          -this.compHeight + this.tailWidth
        }`;
      },
      leftMid() {
        return `${this.compWidth + this.tailWidth - 1} ${
          -this.compHeight / 2 - this.tailWidth / 2
        }, 
          ${this.tailOrigin}  ${this.tailHeight}, ${this.compWidth + this.tailWidth - 1} ${
          -this.compHeight / 2 + this.tailWidth / 2
        }`;
      },
      leftTop() {
        return `${this.compWidth + this.tailWidth - 1} 0, 
          ${this.tailOrigin}  ${this.tailHeight}, ${this.compWidth + this.tailWidth - 1} ${-this
          .tailWidth}`;
      },
      rightBottom() {
        return `1 ${-this.compHeight}, ${this.tailOrigin}  ${this.tailHeight}, 1 ${
          -this.compHeight + this.tailWidth
        }`;
      },
      rightMid() {
        return `1 ${-this.compHeight / 2 - this.tailWidth / 2}, 
          ${this.tailOrigin}  ${this.tailHeight}, 1 ${-this.compHeight / 2 + this.tailWidth / 2}`;
      },
      rightTop() {
        return `1 0, ${this.tailOrigin}  ${this.tailHeight}, 1 ${-this.tailWidth}`;
      },
      tailPoints() {
        const topPosition =
          this.tailOrigin <= 0
            ? this.topRight
            : this.tailOrigin < this.compWidth
            ? this.topMid
            : this.topLeft;

        const bottomPosition =
          this.tailOrigin <= 0
            ? this.bottomRight
            : this.tailOrigin <= this.tailWidth + this.compWidth
            ? this.bottomMid
            : this.bottomLeft;
        const midLeftPos =
          this.tailHeight >= 0
            ? this.leftTop
            : this.tailHeight <= -this.compHeight
            ? this.leftBottom
            : this.leftMid;
        const midRightPos =
          this.tailHeight >= 0
            ? this.rightTop
            : this.tailHeight <= -this.compHeight
            ? this.rightBottom
            : this.rightMid;

        return this.tailOrigin < this.tailWidth + this.compWidth && this.tailHeight > 0
          ? topPosition
          : this.tailOrigin < this.compWidth + this.tailWidth && this.tailHeight <= -this.compHeight
          ? bottomPosition
          : this.tailOrigin <= 0
          ? midRightPos
          : midLeftPos;
      },
      xScale() {
        return scaleLinear().domain(this.domainX).range(this.rangeX);
      },
      yScale() {
        return scaleLinear().domain(this.domainY).range(this.rangeY).clamp(true);
      },
    },
    watch: {
      confHeight: {
        handler(val) {
          const newVal = Number.isNaN(val) || val == null ? this.height : val;
          this.emitProp('height', newVal);
        },
      },
      confWidth: {
        handler(val) {
          const newVal = Number.isNaN(val) || val == null ? this.width : val;
          this.emitProp('width', newVal);
        },
      },
      editable: {
        deep: true,
        handler(val) {
          this.editing = val;
        },
      },
      left: {
        handler(val) {
          this.emitProp('x', val);
          // this.emitProp('linearX', this.xScale.invert(val));
        },
      },
      top: {
        handler(val) {
          this.emitProp('y', val);
          // this.emitProp('linearY', this.yScale.invert(val));
        },
      },
      linearX: {
        handler() {
          this.dragging = false;
          this.updateTail();
        },
      },
      linearY: {
        handler() {
          this.dragging = false;
          this.updateTail();
        },
      },
      selected: {
        deep: true,
        handler(val) {
          if (!val) {
            this.editing = false;
          }
        },
      },
      tailHeight: {
        handler(val) {
          const args = {
            obj: ['annotations', 'callouts', this.id, 'props', 'tail'],
            key: 'height',
            value: val,
          };

          this.$emit('pfannotationedit', args);
        },
      },
      tailOrigin: {
        handler(val) {
          const args = {
            obj: ['annotations', 'callouts', this.id, 'props', 'tail'],
            key: 'origin',
            value: val,
          };

          this.$emit('pfannotationedit', args);
        },
      },
      tailWidth: {
        handler(val) {
          const args = {
            obj: ['annotations', 'callouts', this.id, 'props', 'tail'],
            key: 'width',
            value: val,
          };

          this.$emit('pfannotationedit', args);
        },
      },
      headingLevel: {
        deep: true,
        handler() {
          // this.resizeEl = 'leftHandle';
          this.updateSize({
            movementX: 1,
          });
        },
      },
    },
    data() {
      return {
        editing: false,
        tailPosition: false,
        dragging: false,
        moveTail: false,
        isMounted: false,
        tailWidth: 16,
        tailHeight: 16,
        tailOrigin: 0,
        // isRight: false,
        confWidth: this.width,
        confHeight: this.height,
        resizeEl: '',
        left: this.x,
        top: this.y,
      };
    },
    methods: {
      editText(e) {
        // turn slot to input w/ value default slot content
        // update ccontent with text
        const textRect = this.$refs.bubbleText ? this.$refs.bubbleText.getBoundingClientRect() : 0;
        this.confHeight = textRect.height - 1;

        this.emitProp('content', e.target.innerText);
      },
      emitProp(key, value) {
        const args = {
          obj: ['annotations', 'callouts', this.id, 'props'],
          key,
          value,
        };

        this.$emit('pfannotationedit', args);
      },
      updateLinear() {
        // const origin = this.tailOrigin;
        // const width = this.tailWidth;
        // const height = this.tailHeight;

        this.emitProp('linearX', this.xScale.invert(this.left));
        this.emitProp('linearY', this.yScale.invert(this.top));
        // this.emitProp('tail', { origin, width, height });
      },
      updateTail() {
        const { width, height, origin } = this.tail ?? {};

        this.tailOrigin = origin ?? this.tailOrigin;
        this.tailWidth = width ?? this.tailWidth;
        this.tailHeight = height ?? this.tailHeight;
      },
      handler() {
        const textRect = this.$refs.bubbleText ? this.$refs.bubbleText.getBoundingClientRect() : 0;
        this.confWidth = textRect.width;
        this.confHeight = textRect.height - 1;
      },
      clicked(e) {
        this.trap(e);
        if (!this.selected) {
          this.$emit('click');
        }
        this.editing = true;
      },
      repositioning(e, tail = false) {
        this.trap(e);
        if (!this.selected) {
          this.$emit('click');
        }

        this.moveTail = tail;
        this.dragStart(e);
        document.addEventListener('mouseup', this.dragEnd);
      },
      drag(e) {
        const { offsetX, offsetY } = this.dragging;
        this.trap(e);

        // reposition bubble
        const xMove = isNaN(e.pageX - offsetX) ? 0 : e.pageX - offsetX;
        const yMove = isNaN(e.pageY - offsetY) ? 0 : e.pageY - offsetY;

        this.left += xMove;
        this.top += yMove;

        if (!this.moveTail) {
          this.tailHeight -= yMove;
          this.tailOrigin -= xMove;
        }

        this.updateLinear();

        this.dragging = {
          offsetX: e.pageX,
          offsetY: e.pageY,
        };
      },
      dragEnd() {
        const { drag } = this;
        document.body.removeEventListener('mousemove', this.drag);
      },
      dragStart(e) {
        // const { handle, menu } = this.$refs;

        this.dragging = {
          offsetX: e.pageX,
          offsetY: e.pageY,
        };

        document.body.addEventListener('mousemove', this.drag);
      },
      resizing(e, ref) {
        this.isResizing = true;
        this.resizeEl = ref;
        this.trap(e);

        document.addEventListener('mousemove', this.updateSize);
        document.addEventListener('mouseup', this.endListeners);
      },
      trap(event) {
        event.stopPropagation();
        event.preventDefault();
      },
      updatePosition(e) {
        this.left += e.offsetX;
        this.top += e.offsetY;

        this.updateLinear();
      },
      updateSize(e) {
        if (!this.$refs.bubbleText) {
          return;
        }
        const textRect = this.$refs.bubbleText.getBoundingClientRect();
        const diff = e.movementX || 0;

        if (this.resizeEl === 'leftHandle') {
          if (this.compWidth - diff > 60) {
            this.left += diff;

            this.confWidth = this.compWidth - diff;
            this.tailOrigin = this.confWidth <= 60 ? this.tailOrigin : this.tailOrigin - diff;
          }
        } else if (this.compWidth + diff > 60) {
          this.confWidth = this.compWidth + diff;
        }

        this.updateLinear();
        this.tailHeight =
          this.compHeight > textRect.height
            ? this.tailHeight + (this.compHeight - textRect.height)
            : this.compHeight === textRect.height
            ? this.tailHeight
            : this.tailHeight + (this.compHeight - textRect.height);
        this.confHeight = textRect.height;
      },
      endListeners() {
        document.removeEventListener('mousemove', this.updateSize);
        document.removeEventListener('mousemove', this.drag);
        document.removeEventListener('mouseup', this.endListeners);
      },
    },
    mounted() {
      this.isMounted = true;
      this.editing = this.editable;
      this.confWidth = this.width;
      this.confHeight = this.height;

      this.updateTail();
      this.handler();
    },
    beforeDestroy() {
      this.emitProp('linearX', this.xScale.invert(this.left));
      this.emitProp('linearY', this.yScale.invert(this.top));
    },
  };
</script>

<style lang="scss">
  @import '@mds/constants';
  @import '@mds/typography';
  @import '@mce/visual-language';

  .pf-annotation-bubble {
    &__container {
      // pointer-events: none;
      position: absolute;
    }
  }

  .pf-annotation--resizer {
    position: absolute;
    height: 100%;
    left: 0;
    top: 0;
    width: 100%;
    min-width: 60px;

    // pointer-events: none;

    &_handle {
      align-items: center;
      display: flex;
      justify-content: center;
      pointer-events: auto;
      position: absolute;

      &.x {
        cursor: ew-resize;
        height: 100%;
        top: 0;
        width: 5px;
      }

      &.left {
        left: -5px;
      }

      &.right {
        right: -5px;
      }
    }

    &_inner {
      background: transparent;
      border: 2px solid $mds-interactive-color-primary-default;

      // border-radius: 2px;
      opacity: 0;
      position: absolute;
      transition: opacity 0.2s;

      .x & {
        height: 50%;
        max-height: 40px;
        min-height: 10px;
        width: 5px;
      }

      .pf-annotation--selected & {
        opacity: 1;
      }
    }
  }

  .pf-annotation-bubble-text {
    background-color: $mds-background-color-white;
    border: $mds-border-container;
    box-shadow: $mds-box-shadow-drop;
    outline: none;
    padding: $mce-space-1-x;
    position: absolute;
    width: 100%;
    min-width: 60px;

    &__header {
      cursor: text;

      &-left {
        text-align: left;
      }

      &-center {
        text-align: center;
      }

      &-right {
        text-align: right;
      }

      &-container {
        position: relative;

        .pf-annotation-bubble--config &:hover {
          background-color: #4caf4f4b;
        }

        h1 {
          @include mds-level-1-heading();

          margin: 0;
        }

        h2 {
          @include mds-level-2-heading();

          margin: 0;
        }

        h3 {
          @include mds-level-3-heading();

          margin: 0;
        }

        h4 {
          @include mds-level-4-heading();

          margin: 0;
        }

        h5 {
          @include mds-level-5-heading();

          margin: 0;
        }

        h6 {
          @include mds-level-6-heading();

          margin: 0;
        }
      }
    }

    .pf-annotation-bubble__right & {
      border-radius: $mds-border-radius-panel $mds-border-radius-panel $mds-border-radius-panel 0;
    }

    .pf-annotation-bubble__left & {
      border-radius: $mds-border-radius-panel $mds-border-radius-panel 0 $mds-border-radius-panel;
    }

    .pf-annotation-bubble__bottom & {
      border-radius: $mds-border-radius-panel 0 $mds-border-radius-panel $mds-border-radius-panel;
    }

    .pf-annotation-bubble__top & {
      border-radius: 0 $mds-border-radius-panel $mds-border-radius-panel $mds-border-radius-panel;
    }

    .pf-annotation-bubble__mid & {
      border-radius: $mds-border-radius-panel;
    }

    .pf-annotation--selected & {
      outline: $mds-interactive-color-primary-default solid 1px;
    }
  }

  .pf-annotation-bubble-tail {
    .pf-annotation-bubble--config &__line:hover {
      stroke-width: 3px;
      stroke: $mds-interactive-color-primary-default;
    }

    &__svg {
      overflow: visible;
      position: absolute;
    }

    &__line {
      fill: $mds-background-color-white;
      stroke: $mds-color-neutral-80;
      stroke-width: 1px;
      box-shadow: $mds-box-shadow-drop $mds-box-shadow-drop $mds-box-shadow-drop;
    }

    .pf-annotation--selected & {
      stroke: $mds-interactive-color-primary-default;
    }
  }
</style>
